/**
 * RequireUnauth - Route guard for unauthenticated routes
 * 
 * Prevents authenticated users from accessing login/signup pages
 * and redirects them to the appropriate destination.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { auth } from '../../../firebase/firebase';
import AuthLoadingScreen from '../../shared/AuthLoadingScreen';

function RequireUnauth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';

  // Clear any problematic flags that might cause infinite loading on auth pages
  useEffect(() => {
    // Clear account deletion flag if we're on auth pages (user should be able to sign in)
    if (location.pathname === '/login' || location.pathname === '/signin' || location.pathname === '/signup') {
      sessionStorage.removeItem('accountDeletionInProgress');
      // Also clear any stale auth redirect flags
      const authRedirectFlag = sessionStorage.getItem('auth_redirect_in_progress');
      if (authRedirectFlag === 'true') {
        console.log('Clearing stale auth_redirect_in_progress flag on auth page');
        sessionStorage.removeItem('auth_redirect_in_progress');
      }
      // Clear other potentially problematic flags
      sessionStorage.removeItem('needs_auth_refresh');
      sessionStorage.removeItem('coming_from_signup');
      sessionStorage.removeItem('justLoggedIn');
      console.log('Cleared potentially problematic flags on auth page');
    }
  }, [location.pathname]);

  // Effect to handle redirection after auth state changes
  useEffect(() => {
    if (user && !loading) {
      console.log("RequireUnauth: User is authenticated, checking for redirect...");
      
      // Check if there's a Google sign-in error flag - if so, don't redirect
      const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
      if (googleSignInError) {
        console.log("RequireUnauth: Google sign-in error detected, staying on sign-in page");
        return;
      }
      
      // Check if checkout success is in progress - if so, don't redirect
      if (sessionStorage.getItem('checkout_success_in_progress') === 'true') {
        console.log("RequireUnauth: Checkout success in progress, skipping redirect");
        return;
      }
      
      // SPECIAL CASE: Don't redirect away from success page immediately
      // Allow CheckoutSuccess component to handle the flow
      if (location.pathname.startsWith('/success')) {
        console.log("RequireUnauth: User authenticated on success page, allowing CheckoutSuccess to handle the flow");
        return;
      }
      
      // PRIORITY: Check if user needs email verification first (this overrides everything else)
      const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;
      
      if (isEnterpriseUser && auth.currentUser && !auth.currentUser.emailVerified) {
        console.log("RequireUnauth: Enterprise user needs email verification, redirecting to verify-email page");
        navigate('/verify-email', { replace: true });
        return;
      }
      
      // CRITICAL FIX: Check user's plan level and enterprise status for proper routing
      const isPremiumOrPro = user.planLevel === 'premium' || user.isEnterprise;
      
      // Check if this is a special context where we should allow pricing page access
      const isFromSettings = location.state?.fromSettings;
      const isFromFeature = location.state?.fromFeature;
      const isFromCreateCampaign = location.state?.fromCreateCampaign;
      const isFromCreateTemplate = location.state?.fromCreateTemplate;
      const isFromTemplates = location.state?.fromTemplates;
      const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
      const comingFromSignup = sessionStorage.getItem('coming_from_signup') === 'true';
      
      // Allow premium users on pricing page only in specific contexts
      const isSpecialContext = isFromSettings || isFromFeature || isFromCreateCampaign || 
                              isFromCreateTemplate || isFromTemplates || justSignedUp || comingFromSignup;
      
      // Determine final destination based on user type and status
      let finalDestination;
      
      if (isEnterpriseUser) {
        // Enterprise users NEVER go to pricing - they go to app
        finalDestination = '/app/';
      } else if (isPremiumOrPro && !isSpecialContext) {
        // Non-enterprise premium users go to app (unless in special context)
        finalDestination = '/app/';
      } else {
        // Basic non-enterprise users go to public pricing
        finalDestination = '/pricing';
      }
      
      // If we're already at the correct destination, don't redirect
      if (location.pathname === finalDestination || 
          (finalDestination === '/app/' && location.pathname.startsWith('/app')) ||
          (finalDestination === '/pricing' && location.pathname === '/pricing')) {
        console.log("RequireUnauth: Already at correct destination, no redirect needed");
        return;
      }
      
      console.log("RequireUnauth: Redirecting based on user type", {
        planLevel: user.planLevel,
        isEnterpriseUser: isEnterpriseUser,
        isPremiumOrPro,
        isSpecialContext,
        currentPath: location.pathname,
        finalDestination
      });
      
      // Direct redirect to final destination - no intermediate stops
      navigate(finalDestination, { replace: true });
    }
  }, [user, loading, location.pathname, navigate, location.state]);

  // Only show loading if we're actually loading auth state, or verification is in progress
  if (loading || evInProgress) {
    return <AuthLoadingScreen />;
  }

  // If user is authenticated and we are mid-redirect, show loading screen.
  // Do NOT show this loading screen when we're already on the public pricing page,
  // because it prevents the Pricing page from displaying after Google sign-in.
  const isPublicPricing = location.pathname === '/pricing';
  // Detect if the user will be redirected to the public pricing page (basic, non-enterprise)
  const willRedirectToPricing = user && !user?.isEnterprise && user?.planLevel !== 'premium';
  if (user && !isPublicPricing && !willRedirectToPricing) {
    console.log("RequireUnauth: User authenticated, showing loading screen during redirect");
    return <AuthLoadingScreen />;
  }
  
  // If no user is logged in, show the children (login/signup form)
  return children;
}

export default RequireUnauth;

